/**
 * EarlyAccessModal
 * ────────────────────────────────────────────────────────────────────────
 * Lightweight Parent/Teen waitlist capture used by the homepage's top
 * "Limited Early Access — 90 days free" banner. Replaces the previous
 * full /register page redirect with a 4-field modal: name, email, state,
 * town. POSTs to the existing `/api/waitlist/signup` endpoint with
 * source='early_access_banner' so the existing waitlist tooling sees it.
 *
 * Sets a localStorage flag so the banner doesn't keep prompting once the
 * visitor has joined.
 */
import React, { useState, useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'hibff_early_access_joined';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const hasJoinedEarlyAccess = () => {
  try {
    return !!window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
};

const EarlyAccessModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', state: '', town: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Reset state whenever the modal reopens.
  useEffect(() => {
    if (isOpen) {
      setError('');
      setSubmitting(false);
      setDone(false);
      setForm({ name: '', email: '', state: '', town: '' });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const name = form.name.trim();
    const email = form.email.trim();
    const state = form.state.trim();
    const town = form.town.trim();
    if (!name) return setError('Please enter your name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError('Please enter a valid email.');
    if (!state) return setError('Please enter your state or region.');
    if (!town) return setError('Please enter your town or city.');

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/waitlist/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          first_name: name,
          interest: 'parent',
          state,
          town,
          source: 'early_access_banner',
          landing_page: typeof window !== 'undefined' ? window.location.pathname : '/',
          referrer: typeof document !== 'undefined' ? document.referrer || null : null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Something went wrong. Please try again.');
        setSubmitting(false);
        return;
      }
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ name, email, state, town }));
      } catch {
        // ignore quota errors
      }
      setDone(true);
      setSubmitting(false);
    } catch {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center px-4"
      data-testid="early-access-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-[#1A1209]/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: '#FFF5EA' }}
            >
              <Sparkles size={18} style={{ color: '#F5831F' }} />
            </div>
            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-widest"
                style={{ color: '#F5831F' }}
              >
                Limited Early Access
              </p>
              <h3
                className="text-base font-bold leading-tight"
                style={{ color: '#1A1209' }}
              >
                90 days free at launch
              </h3>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
            aria-label="Close"
            data-testid="early-access-close"
          >
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div className="px-6 py-8 text-center" data-testid="early-access-success">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h4 className="text-lg font-bold text-[#1A1209] mb-2">You&apos;re on the list</h4>
            <p className="text-sm text-gray-600 mb-5 leading-relaxed">
              We&apos;ll email you the moment HiBFF opens to your area — your 90 days are reserved.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl font-semibold text-sm text-white"
              style={{ backgroundColor: '#F5831F' }}
              data-testid="early-access-success-close"
            >
              Continue browsing
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            <p className="text-sm text-gray-600 leading-relaxed">
              Parents — join the waitlist and get <strong>90 days free</strong> when HiBFF
              launches in your area. Just four quick fields, no payment, no spam.
            </p>

            <div>
              <label htmlFor="ea-name" className="block text-xs font-semibold text-gray-700 mb-1">Your name</label>
              <input
                id="ea-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Jane Smith"
                autoComplete="name"
                required
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F5831F] focus:border-transparent"
                data-testid="early-access-name"
              />
            </div>

            <div>
              <label htmlFor="ea-email" className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
              <input
                id="ea-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="jane@example.com"
                autoComplete="email"
                required
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F5831F] focus:border-transparent"
                data-testid="early-access-email"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="ea-state" className="block text-xs font-semibold text-gray-700 mb-1">State / region</label>
                <input
                  id="ea-state"
                  type="text"
                  value={form.state}
                  onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                  placeholder="California"
                  autoComplete="address-level1"
                  required
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F5831F] focus:border-transparent"
                  data-testid="early-access-state"
                />
              </div>
              <div>
                <label htmlFor="ea-town" className="block text-xs font-semibold text-gray-700 mb-1">Town / city</label>
                <input
                  id="ea-town"
                  type="text"
                  value={form.town}
                  onChange={(e) => setForm((f) => ({ ...f, town: e.target.value }))}
                  placeholder="Palo Alto"
                  autoComplete="address-level2"
                  required
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F5831F] focus:border-transparent"
                  data-testid="early-access-town"
                />
              </div>
            </div>

            {error && (
              <p
                className="text-xs text-red-600"
                data-testid="early-access-error"
                role="alert"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-5 py-3 rounded-xl font-semibold text-sm text-white transition-colors disabled:opacity-60"
              style={{ backgroundColor: '#F5831F' }}
              data-testid="early-access-submit"
            >
              {submitting ? 'Joining…' : 'Join the waitlist'}
            </button>

            <p className="text-[11px] text-gray-500 text-center leading-relaxed">
              By joining you agree to our{' '}
              <a href="/privacy" className="underline hover:text-gray-700" target="_blank" rel="noreferrer">privacy policy</a>.
              HiBFF will never sell your information.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default EarlyAccessModal;
